import { useState } from 'react';

import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import styles from './Sheets.module.css';

/**
 * Das Passwort vor den Einstellungen, aus der App heraus gesetzt (Entscheidung des Nutzers,
 * 2026-10-02).
 *
 * **Warum es das gibt:** im Kiosk-Modus (geführter Zugriff) führt kein Weg in die
 * iPad-Einstellungen. Wer das Passwort dort vorher vergessen hat, könnte die App sonst nicht
 * mehr sperren.
 *
 * **Wer darf:** wer den Dialog offen hat — der hat die Sperre schon hinter sich. Das alte
 * Passwort wird nicht noch einmal verlangt.
 *
 * **Wann nicht:** wenn eine Geräteverwaltung (MDM) oder ein QR-Code es vorgibt. Was die App
 * schreibt, landet in den iPad-Einstellungen; darüber stehen nur diese beiden
 * ([[einstellungen-rangfolge]]).
 *
 * Zweimal eingeben, weil ein Tippfehler das Gerät aussperrt — und der Prüfer sieht nicht, was
 * er tippt.
 */
export function PasswortSetzen() {
  const t = useT();
  const gesetzt = useAppStore((s) => s.einstellungenPasswort !== '');
  const quelle = useAppStore((s) => s.quellen.settings_password);
  const setze = useAppStore((s) => s.setEinstellungenPasswort);
  const [neu, setNeu] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [meldung, setMeldung] = useState<{ gut: boolean; text: string } | null>(null);

  const gesperrt = quelle === 'mdm' || quelle === 'qr';

  const setzen = () => {
    if (neu !== nochmal) {
      setMeldung({ gut: false, text: t.settings.passwortUngleich });
      return;
    }
    setze(neu);
    setNeu('');
    setNochmal('');
    setMeldung({ gut: true, text: t.settings.passwortGesetzt });
  };

  const entfernen = () => {
    setze('');
    setNeu('');
    setNochmal('');
    setMeldung({ gut: true, text: t.settings.passwortEntfernt });
  };

  return (
    <div className={styles.stufenBlock}>
      <div className={styles.textNote}>{t.settings.passwortStand(gesetzt)}</div>
      {gesperrt ? (
        <div className={styles.textNote}>
          {quelle === 'mdm' ? t.settings.managedByMdm : t.settings.passwortAusQr}
        </div>
      ) : (
        <>
          <label className={styles.textRow}>
            <span className={styles.textLabel}>{t.settings.passwortNeu}</span>
            <input
              className={styles.textInput}
              type="password"
              autoComplete="new-password"
              value={neu}
              onChange={(e) => setNeu(e.target.value)}
            />
          </label>
          <label className={styles.textRow}>
            <span className={styles.textLabel}>{t.settings.passwortWiederholen}</span>
            <input
              className={styles.textInput}
              type="password"
              autoComplete="new-password"
              value={nochmal}
              onChange={(e) => setNochmal(e.target.value)}
            />
          </label>
          <div className={styles.serverActions}>
            <button
              type="button"
              className={`tap ${styles.stufe}`}
              disabled={neu === ''}
              onClick={setzen}
            >
              {t.settings.passwortSetzen}
            </button>
            {gesetzt && (
              <button type="button" className={`tap ${styles.stufe}`} onClick={entfernen}>
                {t.settings.passwortEntfernen}
              </button>
            )}
          </div>
        </>
      )}
      {meldung && (
        <div
          className={styles.textNote}
          style={{ color: meldung.gut ? 'var(--ok, #0a7d4b)' : 'var(--warn, #b4540a)' }}
        >
          {meldung.text}
        </div>
      )}
    </div>
  );
}
